import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getCPDataWithParams, postCPDataWithoutParams } from 'src/clinic/services'
import { startPortalVoiceCall } from 'src/mentor/components/voice/portalVoiceCallManager'

// Clinic desk → practitioner on duty. The client never names a phone number, and for the on-duty
// routes not even a practitioner: the server resolves who is checked in / rostered for the role.

const BASE = '/api/secure/ca/desk-calls'

export const DESK_CALL_ROLES = {
  screener: { id: 'screener', label: 'Screener', article: 'the screener' },
  therapist: { id: 'therapist', label: 'Therapist', article: 'the therapist' },
  doctor: { id: 'doctor', label: 'Doctor', article: 'the doctor' },
}

function getResponseMessage(response, fallbackMessage) {
  return (
    response?.data?.message ||
    response?.response?.data?.message ||
    response?.message ||
    fallbackMessage
  )
}

export async function previewDeskCall({
  clinicBrandId,
  clinicId,
  role,
  chamberRoomId,
  eventType,
  specialtySlug,
  doctorMentorId,
  skipPractitionerIds,
} = {}) {
  if (!clinicId || !role) {
    return { ok: false, message: 'Clinic and role are required.' }
  }
  const response = await getCPDataWithParams(`${BASE}/preview`, {
    clinicBrandId,
    clinicId,
    role,
    chamberRoomId,
    eventType,
    specialtySlug,
    doctorMentorId,
    skipPractitionerIds: skipPractitionerIds?.length ? skipPractitionerIds.join(',') : undefined,
  })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return { ok: true, ...(response?.data?.data || {}) }
  }
  return { ok: false, message: getResponseMessage(response, 'Unable to find who is on duty.') }
}

// Creates the desk-call request and (unless notifyOnly) returns the softphone bootstrap payload.
export async function startDeskCallRequest(payload = {}) {
  const response = await postCPDataWithoutParams(`${BASE}/start`, payload)
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = response?.data?.data || {}
    return {
      ok: true,
      providerType: data?.providerType || data?.route?.providerType,
      accessToken: data?.accessToken,
      requestToken: data?.requestToken,
      providerTransportToken:
        data?.providerTransportToken || data?.providerSession?.providerTransportToken || '',
      renewalToken: data?.renewalToken,
      callSessionId: data?.callSessionId,
      identity: data?.identity,
      expiresInSeconds: data?.expiresInSeconds,
      route: data?.route || {},
      providerSession: data?.providerSession || {},
      deskCallRequest: data?.deskCallRequest || null,
      nobodyOnDuty: !!data?.nobodyOnDuty,
      message: data?.message || '',
      rawResponse: response,
    }
  }
  return {
    ok: false,
    message: getResponseMessage(response, 'Unable to place the desk call.'),
    rawResponse: response,
  }
}

// Places the call through the shared browser softphone. Resolves with the desk-call request view
// (or {nobodyOnDuty: true}); rejects with a friendly error when the softphone could not start.
export async function placeDeskCall({
  clinicBrandId,
  clinicId,
  role,
  chamberRoomId,
  eventType,
  specialtySlug,
  doctorMentorId,
  bookedEventId,
  practitionerId,
  skipPractitionerIds,
  reason,
  retryOfRequestId,
  uiSurface = 'clinic-assistant-desk-call',
} = {}) {
  let prepared = null
  const bootstrapOverride = async () => {
    prepared = await startDeskCallRequest({
      clinicBrandId,
      clinicId,
      role,
      chamberRoomId,
      eventType,
      specialtySlug,
      doctorMentorId,
      bookedEventId,
      practitionerId,
      skipPractitionerIds,
      reason,
      retryOfRequestId,
      uiSurface,
    })
    if (prepared?.ok && prepared?.nobodyOnDuty) {
      // Surface as a controlled failure so the softphone never starts.
      const error = new Error(prepared?.message || 'Nobody is on duty right now.')
      error.nobodyOnDuty = true
      error.deskCallRequest = prepared?.deskCallRequest || null
      throw error
    }
    return prepared
  }

  try {
    await startPortalVoiceCall({
      actorRole: 'clinic-assistant',
      clinicBrandId,
      clinicId,
      destinationType: `on-duty-${role}`,
      uiSurface,
      bootstrapOverride,
      stateExtras: { deskCallRole: role },
    })
  } catch (error) {
    if (error?.nobodyOnDuty) {
      return { ok: true, nobodyOnDuty: true, message: error.message, deskCallRequest: error.deskCallRequest }
    }
    throw error
  }
  return { ok: true, nobodyOnDuty: false, deskCallRequest: prepared?.deskCallRequest || null }
}

export async function notifyDeskCallOnWhatsapp(requestId) {
  const response = await postCPDataWithoutParams(`${BASE}/${requestId}/notify`, {})
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return { ok: true, deskCallRequest: response?.data?.data?.deskCallRequest || null }
  }
  return { ok: false, message: getResponseMessage(response, 'Unable to send the WhatsApp message.') }
}

export async function getDeskCall(requestId) {
  const response = await getCPDataWithParams(`${BASE}/${requestId}`, {})
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return { ok: true, deskCallRequest: response?.data?.data?.deskCallRequest || null }
  }
  return { ok: false, message: getResponseMessage(response, 'Unable to load the desk call.') }
}

export async function listRecentDeskCalls({ clinicId, limit = 10 } = {}) {
  const response = await getCPDataWithParams(`${BASE}/recent`, { clinicId, limit })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return { ok: true, requests: response?.data?.data?.requests || [] }
  }
  return { ok: false, message: getResponseMessage(response, 'Unable to load recent desk calls.'), requests: [] }
}

export function describeResolvedVia(request) {
  switch (request?.resolvedVia) {
    case 'checked-in-room':
    case 'checked-in-location':
      return `Checked in${request?.targetRoomTitle ? ` · ${request.targetRoomTitle}` : ''}`
    case 'rostered-now':
      return 'Rostered now, not checked in'
    case 'rostered-next':
      return 'Rostered next'
    case 'fallback':
      return 'Backup contact'
    case 'explicit':
      return 'Selected by you'
    default:
      return ''
  }
}

export function describeDeskCallStatus(request) {
  switch (request?.status) {
    case 'ringing':
      return 'Ringing…'
    case 'answered':
      return 'Answered'
    case 'missed':
    case 'notified':
      return 'No answer · messaged on WhatsApp'
    case 'escalated':
      return 'No answer · next practitioner messaged'
    case 'acknowledged':
      return 'On their way'
    case 'called-back':
      return 'Called the desk back'
    case 'nobody-on-duty':
      return 'Nobody on duty'
    case 'cancelled':
      return 'Cancelled'
    case 'failed':
      return 'Could not connect'
    case 'expired':
      return 'Expired'
    default:
      return request?.status || ''
  }
}

// ---- Inbound desk number + assignment-mobile verification (call from your own phone) ----

export async function getDeskInboundInfo({ clinicId } = {}) {
  const response = await getCPDataWithParams(`${BASE}/inbound`, { clinicId })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return { ok: true, ...(response?.data?.data || {}) }
  }
  return { ok: false, message: getResponseMessage(response, 'Unable to load the desk number.') }
}

export async function startDeskMobileVerification({ clinicId, mobile } = {}) {
  const response = await postCPDataWithoutParams(`${BASE}/verify-mobile/start`, { clinicId, mobile })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return { ok: true, ...(response?.data?.data || {}), message: response?.data?.message }
  }
  return { ok: false, message: getResponseMessage(response, 'Unable to send the verification code.') }
}

export async function confirmDeskMobileVerification({ clinicId, sessionId, otp } = {}) {
  const response = await postCPDataWithoutParams(`${BASE}/verify-mobile/confirm`, { clinicId, sessionId, otp })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return { ok: true, ...(response?.data?.data || {}), message: response?.data?.message }
  }
  return { ok: false, message: getResponseMessage(response, 'That code did not work.') }
}
